import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal } from "Components/General/Modal/GenericModal";
import { observer } from "mobx-react";
import { EmailSendForm } from "./EmailSendForm";
import {
  EmailSubmitAction,
  emailSubmitStore,
  MailInitialRecipients,
  submitMessageMap,
} from "../Stores/EmailSubmitStore";
import { notificationError, notificationSuccess } from "utils/libHelpers/AntdNotification";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { withTranslation, WithTranslation, useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { AttachProjectFiles } from "../Pages/AttachProjectFiles";
import { AccessRightsForm } from "Components/General/AccessRightsForm";

export type fileAccess = "PIN" | "ACCESS_TYPE";
export type MailType = "reply" | "forward" | "reply_all" | "attach" | "draft";

interface AccessRightsModalProps extends WithTranslation {
  isVisible: boolean;
  toggleModal: () => void;
  accessType: fileAccess;
  children: React.ReactNode;
}

const AccessRightsModal = withTranslation(I18N_NS.PROJECT)(
  ({ isVisible, toggleModal, accessType, t, children }: AccessRightsModalProps) => (
    <Modal
      title={t(`Send email – define ${accessType === "PIN" ? "external" : "internal"} access`)}
      open={isVisible}
      footer={null}
      destroyOnHidden={true}
      maskClosable={false}
      onCancel={toggleModal}
      width={`100%`}
      style={{ maxWidth: 800 }}
      zIndex={1012}
    >
      {children}
    </Modal>
  )
);

interface EmailSendModalProps extends ModalComponentProps {
  mailData?: API_MailResponse & MailInitialRecipients;
  mailType?: MailType;
  mailSubmitAction?: EmailSubmitAction;
  onSubmitCallback?: (id?: string) => void;
}

export interface MailMissingAccessInformation {
  externalEmails?: string[];
  internalUserNames?: string[];
}
interface EmailSendModalState {
  isAccessVisible: boolean;
  accessException: fileAccess;
  /*
  list of usernames that are not allowed to access mail attachments. Passed with 409 http status and code:1056
 */
  errorUsers: MailMissingAccessInformation;
  errorViewersFiles: { withoutViewer: string[]; viewerOnly: string[] };
  isFilesVisible: boolean;
  isExternalFilesWithProjectFiles: boolean;
}

export const EmailSendModal = observer(
  ({
    onSubmitCallback, hideModal, setConfirmOnCancel, mailData, mailType, mailSubmitAction
  }: EmailSendModalProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const [state, setState] = useState<EmailSendModalState>({
      isAccessVisible: false,
      accessException: null,
      isFilesVisible: false,
      errorUsers: null,
      errorViewersFiles: null,
      isExternalFilesWithProjectFiles: false,
    });

    useEffect(() => {
      return () => {
        emailSubmitStore.clearAll();
      };
    }, []);

    const setIsExternalFilesWithProjectFiles = useCallback((postObject: API_MailRequest) => {
      setState((prevState) => ({
        ...prevState,
        isExternalFilesWithProjectFiles: postObject?.attachments?.some((item) => item.includes("@temp")),
      }));
    }, []);

    const handleSubmit = useCallback(
      async (postObject: API_MailRequest, action: EmailSubmitAction = "sent") => {
        try {
          setState((prevState) => ({ ...prevState, accessException: null }));
          emailSubmitStore.update(postObject);
          await emailSubmitStore.submitAll(action);
          setConfirmOnCancel(false);
          setState((prevState) => ({ ...prevState, isAccessVisible: false }));
          notificationSuccess(t("Success"), t(submitMessageMap[action].success));
          hideModal();
          onSubmitCallback && onSubmitCallback(action !== "sent" ? emailSubmitStore.data.id : "");
        } catch (err) {
          setIsExternalFilesWithProjectFiles(postObject);
          if (err.response && err.response.status === 409 && err.response.data) {
            const { code } = err.response.data;
            switch (code) {
              case 1051:
                notificationError(t(`Email was not sent`), t("Some of the emails are not valid"));
                break;
              case 1069: {
                const addresses: string[] = err.response.data.addresses || [];
                const message = (
                  <>
                    <p>
                      {t("Please check the part after @ (domain) of the following invalid receiver email addresses:")}
                      <ul>
                        {addresses.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </p>
                  </>
                );
                notificationError(t(`409`), message, true);
                break;
              }
              case 1054:
                notificationError(t("Error"), t("Sharing files with external users is prohibited by project settings"));
                break;
              case 1055:
                setState((prevState) => ({
                  ...prevState,
                  accessException: "PIN",
                  errorUsers: err.response.data,
                  errorViewersFiles: {
                    withoutViewer: err.response.data?.filesWithoutViewer || [],
                    viewerOnly: err.response.data?.officeViewerOnly || [],
                  },
                }));
                if (!state.isAccessVisible) {
                  toggleModal("isAccessVisible");
                }
                break;
              case 1056:
                setState((prevState) => ({
                  ...prevState,
                  accessException: "ACCESS_TYPE",
                  errorUsers: err.response.data,
                  errorViewersFiles: {
                    withoutViewer: err.response.data?.filesWithoutViewer || [],
                    viewerOnly: err.response.data?.officeViewerOnly || [],
                  },
                }));
                if (!state.isAccessVisible) {
                  toggleModal("isAccessVisible");
                }
                break;
              default:
                setState((prevState) => ({
                  ...prevState,
                  accessException: null,
                  errorUsers: null,
                }));
                if (state.isAccessVisible) {
                  toggleModal("isAccessVisible");
                }
                break;
            }
          } else {
            setState((prevState) => ({ ...prevState, accessException: null }));
            notificationError(t("Error"), t("Unable to send email"));
            throw err;
          }
        }
      },
      [t, onSubmitCallback, hideModal, setConfirmOnCancel, setIsExternalFilesWithProjectFiles]
    );

    const toggleModal = useCallback((key: keyof EmailSendModalState) => {
      setState((prevState) => {
        const newState = { ...prevState, [key]: !prevState[key] } as EmailSendModalState;
        if (key === "isAccessVisible" && !newState.accessException) {
          newState.errorUsers = null;
          newState.errorViewersFiles = null;
        }
        return newState;
      });
    }, []);

    const hintText = useMemo(() => {
      const { errorUsers, accessException } = state;
      // DEV-6112 if errorUsers email lists are empty - don't return hint text
      if (!errorUsers || (!errorUsers.internalUserNames?.length && !errorUsers.externalEmails?.length)) {
        return null;
      }
      return (
        <>
          {accessException === "ACCESS_TYPE" ? (
            <p>{t(`The following users:`)}</p>
          ) : (
            <p>{t(`The following external recipients:`)}</p>
          )}
          {errorUsers?.externalEmails && (
            <ul>
              {errorUsers.externalEmails.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          {errorUsers?.internalUserNames && (
            <ul>
              {errorUsers.internalUserNames.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          <p>
            {t(
              `have no access rights for the attached files. You can now allow them to access these files when they open this email. `
            )}
            {accessException === "ACCESS_TYPE" ? (
              <span>{t("Access rights within the Documents module remain untouched (still no access there).")}</span>
            ) : (
              <span></span>
            )}
          </p>
        </>
      );
    }, [state.errorUsers, state.accessException, t]);

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <AccessRightsModal
          isVisible={state.isAccessVisible}
          toggleModal={() => toggleModal("isAccessVisible")}
          accessType={state.accessException}
        >
          <AccessRightsForm
            fromModule="MAIL"
            accessType={state.accessException}
            onClose={() => toggleModal("isAccessVisible")}
            onSubmit={handleSubmit}
            hintText={hintText}
            problemFiles={state.errorViewersFiles}
            isLoading={emailSubmitStore.state === "LOADING"}
            disableZip={state.isExternalFilesWithProjectFiles}
          />
        </AccessRightsModal>
        <Modal
          title={t("Attach files from project")}
          footer={null}
          open={state.isFilesVisible}
          destroyOnHidden={true}
          maskClosable={false}
          onCancel={() => toggleModal("isFilesVisible")}
          width={`100%`}
          style={{ maxWidth: 800 }}
          zIndex={1012}
        >
          <AttachProjectFiles toggleModal={() => toggleModal("isFilesVisible")} />
        </Modal>
        <EmailSendForm
          toggleFilesModal={() => toggleModal("isFilesVisible")}
          initialData={mailData}
          initialDataType={mailType}
          mailSubmitAction={mailSubmitAction}
          onSubmit={handleSubmit}
          setConfirmOnCancel={setConfirmOnCancel}
          isLoading={emailSubmitStore.state === "LOADING"}
        />
      </div>
    );
  }
);
